import { TRANSCRIPT_CRAWL_HOSPITALS } from '@/constant';

export type MeetingTranscriptionHospital = {
    id: string;
    name: string;
};

type NamedHospital = {
    id: string;
    name?: string | null;
};

export const normalizeHospitalName = (name: string | null | undefined): string =>
    String(name ?? '')
        .trim()
        .toLowerCase();

export const isTranscriptCrawlHospitalName = (name: string | null | undefined): boolean =>
    TRANSCRIPT_CRAWL_HOSPITALS.includes(normalizeHospitalName(name));

export const filterTranscriptCrawlHospitals = <T extends { name?: string | null }>(hospitals: T[]): T[] =>
    hospitals.filter((hospital) => isTranscriptCrawlHospitalName(hospital.name));

export const hasTranscriptCrawlHospital = (hospitals: Array<{ name?: string | null }> | null | undefined): boolean =>
    Boolean(hospitals?.some((hospital) => isTranscriptCrawlHospitalName(hospital.name)));

export const isMeetingTranscriptionHospitalContext = (pathname: string): boolean => pathname.includes('/admin/hospitals/');

const toHospital = (hospital: NamedHospital): MeetingTranscriptionHospital => ({
    id: hospital.id,
    name: hospital.name?.trim() || hospital.id
});

/** Resolves the active hospital from the route (hospital admin) or header selection (launch hospitals). */
export const resolveMeetingTranscriptionHospital = ({
    hospitalIdFromRoute,
    currentHospitalId,
    adminHospitals,
    assignedHospitals
}: {
    hospitalIdFromRoute?: string | null;
    currentHospitalId?: string | null;
    adminHospitals: NamedHospital[];
    assignedHospitals: NamedHospital[];
}): MeetingTranscriptionHospital | null => {
    if (hospitalIdFromRoute) {
        const fromAdmin = adminHospitals.find((hospital) => hospital.id === hospitalIdFromRoute);
        if (fromAdmin) {
            return toHospital(fromAdmin);
        }
        const fromAssigned = assignedHospitals.find((hospital) => hospital.id === hospitalIdFromRoute);
        if (fromAssigned) {
            return toHospital(fromAssigned);
        }
        return { id: hospitalIdFromRoute, name: hospitalIdFromRoute };
    }

    const launchHospitals = filterTranscriptCrawlHospitals(assignedHospitals).map(toHospital);
    if (currentHospitalId) {
        const headerHospital = launchHospitals.find((hospital) => hospital.id === currentHospitalId);
        if (headerHospital) {
            return headerHospital;
        }
    }

    return launchHospitals.length === 1 ? launchHospitals[0] : null;
};
